// Shop setting templates — the pricing SKELETON a shop starts from.
//
// A template is pure data: weight tiers + coat split + special-breed rows +
// a service list + add-ons. Prices are deliberately left at 0; what the
// template saves the owner is the *structure* (how many size bands, which
// breeds get their own price, which services exist), which is the fiddly
// part to get right and the part that is painful to change later.
//
// Picked once in the first-login wizard and re-appliable forever from
// 設定 → 服務 → 套用設定模板.
//
// SHAPE NOTE — this file writes the CURRENT pricing model, not the legacy one:
//   tenant.pricingConfig.tiers      shop-wide weight bands  [{id,label,minW,maxW}]
//   tenant.pricingConfig.coatSplit  true = separate 短毛/長毛 prices
//   tenant.pricingConfig.hugeExtra  over-cap surcharge {baseW,perStep,stepPrice}
//   tenant.specialBreeds            shop-wide breed rows
//   service.matrixPricing.byTier    { [tierId]: {short,long,mobile,na,...} }
//   service.matrixPricing.byBreed   { [breedId]: { [tierId]: {...} } }
// (seedDefaults.js used to write the older per-service `matrixPricing.sizes`
// array. Both are still read by svcTierPrices/getPricingDetail, but nothing
// new should be written in that shape.)

import { patchTenant, upsertService, upsertAddon } from "./api";
import { getLocale } from "./i18n";

// Service names, tier labels and breed names are written INTO the database —
// they are data, not UI, so the language toggle can never re-translate them
// afterwards. A shop running in English must therefore be seeded in English
// at apply time. Japanese owner UI already falls back to English elsewhere,
// so it does here too.
const isEn = () => getLocale() !== "zh-HK";
const pickName = (zh, en) => (isEn() && en ? en : zh);

const uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
        (Number(c) ^ (Math.random() * 16) >> Number(c) / 4).toString(16)));

// Tier + breed ids are STABLE per template (not random) so that re-applying
// the same template lands prices back on the same byTier / byBreed keys
// instead of orphaning them.
const tier = (id, label, labelEn, minW, maxW) => ({ id, label: pickName(label, labelEn), minW, maxW });
const breed = (id, name, nameEn, catalogBreedIds, names) => ({
  id,
  name: pickName(name, nameEn),
  names: names || [name],
  catalogBreedIds,
  catalogBreedId: catalogBreedIds[0] || "",
  excludeHugeExtra: false,
});

// The five add-ons every template below shares, in the order the seed used.
const CORE_ADDONS = [
  { name: "洗耳", nameEn: "Ear cleaning", price: 50, estimatedMinutes: 10 },
  { name: "剪甲", nameEn: "Nail trim", price: 50, estimatedMinutes: 10 },
  { name: "刷牙服務", nameEn: "Teeth brushing", price: 100, estimatedMinutes: 10 },
  { name: "貴婦腳", nameEn: "Poodle feet", price: 100, estimatedMinutes: 10 },
  { name: "解結", nameEn: "De-matting", price: 100, estimatedMinutes: 10 },
];

// $0-base, owner-only service for a walk-in that wants ear-clean / nail-trim
// and nothing else. Static pricing (no matrix), hidden from the public page.
const QUICK_CARE = {
  name: "快速護理 (洗耳/剪甲)",
  nameEn: "Quick care (ears / nails)",
  petType: "dog",
  estimatedMinutes: 15,
  displayOrder: 99,
  customerVisible: false,
  pricingMode: "static",
};

const SPA_SERVICES = [
  { name: "SPA - A", nameEn: "SPA - A", kind: "spa", estimatedMinutes: 10, displayOrder: 1, description: "舒緩肌膚 SPA：溫泉鹽浴 + 深層滋潤護理，適合皮膚乾燥／脫毛季節。約 30 分鐘。", descriptionEn: "Soothing SPA: hot-spring salt bath + deep conditioning, for dry skin and shedding season. About 30 min." },
  { name: "SPA - B", nameEn: "SPA - B", kind: "spa", estimatedMinutes: 10, displayOrder: 2, description: "去味抗菌 SPA：藥用洗劑 + 草本爽身霧，針對體味重／皮膚問題嘅毛孩。約 30 分鐘。", descriptionEn: "Deodorising SPA: medicated wash + herbal mist, for strong odour or skin trouble. About 30 min." },
  { name: "SPA - C", nameEn: "SPA - C", kind: "spa", estimatedMinutes: 10, displayOrder: 3, description: "亮毛蓬鬆 SPA：絲蛋白護毛 + 吹乾造型，毛色更亮、更易梳理。約 25 分鐘。", descriptionEn: "Shine SPA: silk-protein treatment + blow-dry styling, for a glossier, easier-to-brush coat. About 25 min." },
];

export const SETTING_TEMPLATES = [
  // ── ① 簡單 ────────────────────────────────────────────────────────────
  // One groomer, prices by size and nothing else. Six numbers to fill in.
  {
    key: "simple",
    emoji: "🌱",
    name: "簡單",
    nameEn: "Simple",
    subtitle: "一人小店",
    subtitleEn: "Solo shop",
    tagline: "3 級體型 · 單一價 · 冇特殊品種",
    taglineEn: "3 size tiers · one price each · no special breeds",
    desc: "最少嘅設定。淨係按體重分 3 級，每級一個價。啱一人經營、價目表簡單嘅小店。",
    descEn: "The smallest possible setup: three weight bands, one price each. For a one-groomer shop with a simple price list.",
    tiers: [
      tier("t-s", "小型", "Small", 0.1, 6),
      tier("t-m", "中型", "Medium", 6.1, 12),
      tier("t-l", "大型", "Large", 12.1, 999),
    ],
    coatSplit: false,
    hugeExtra: { baseW: 12, perStep: 5, stepPrice: 0 },
    breeds: [],
    services: [
      { name: "沖涼全套", nameEn: "Full bath", petType: "dog", estimatedMinutes: 60, displayOrder: 1 },
      { name: "沖涼剪毛", nameEn: "Bath & haircut", petType: "dog", estimatedMinutes: 120, displayOrder: 2 },
      QUICK_CARE,
    ],
    addons: CORE_ADDONS.slice(0, 3),
  },

  // ── ② 標準 ────────────────────────────────────────────────────────────
  // What the old seed produced, plus the three breed groups that actually
  // break a size-only price list in HK.
  {
    key: "standard",
    emoji: "⭐",
    name: "標準",
    nameEn: "Standard",
    subtitle: "大部分美容店",
    subtitleEn: "Most shops",
    recommended: true,
    tagline: "5 級體型 · 短/長毛分開 · 3 組特殊品種",
    taglineEn: "5 size tiers · short/long coat · 3 breed groups",
    desc: "大部分寵物美容店嘅做法。5 級體型，短毛同長毛分開收費，另加貴婦系、雙層厚毛、捲毛梗犬三組獨立價。包狗、貓服務同 SPA。",
    descEn: "How most grooming shops price. Five size tiers, separate short- and long-coat prices, plus their own rates for poodles, double-coated breeds and curly terriers. Includes dog, cat and SPA services.",
    tiers: [
      tier("t-s", "小型", "Small", 0.1, 5),
      tier("t-m", "中型", "Medium", 5.1, 10),
      tier("t-l", "大型", "Large", 10.1, 15),
      tier("t-xl", "超大", "X-Large", 15.1, 20),
      tier("t-xxl", "巨型", "Giant", 20.1, 999),
    ],
    coatSplit: true,
    hugeExtra: { baseW: 25, perStep: 5, stepPrice: 0 },
    breeds: [
      breed("sb-poodle", "貴婦系", "Poodle family", ["poodle", "cockapoo", "maltipoo", "poochon", "shihpoo"],
        ["貴婦犬", "可卡布", "馬爾貴婦", "比熊貴婦", "西施貴婦"]),
      breed("sb-double", "雙層厚毛", "Double-coated", ["pomeranian", "samoyed", "husky", "golden", "shiba"],
        ["松鼠犬", "薩摩耶", "哈士奇", "金毛尋回", "柴犬"]),
      breed("sb-curly", "捲毛梗犬", "Curly terriers", ["bichon", "schnauzer", "westie"],
        ["比熊犬", "雪納瑞", "西高地白爹利"]),
    ],
    services: [
      { name: "沖涼全套", nameEn: "Full bath", petType: "dog", estimatedMinutes: 60, displayOrder: 1 },
      { name: "沖涼剪毛 - 留頭尾", nameEn: "Bath & haircut — head/tail kept", petType: "dog", estimatedMinutes: 120, displayOrder: 2 },
      { name: "沖涼剪毛 - 半手剪", nameEn: "Bath & haircut — part scissor", petType: "dog", estimatedMinutes: 120, displayOrder: 3 },
      { name: "沖涼剪毛 - 全手剪", nameEn: "Bath & haircut — full scissor", petType: "dog", estimatedMinutes: 120, displayOrder: 4 },
      { name: "貓沖涼全套", nameEn: "Cat full bath", petType: "cat", estimatedMinutes: 60, displayOrder: 5 },
      { name: "貓沖涼剷毛 - 留頭尾手仔", nameEn: "Cat bath & shave — head/tail/paws kept", petType: "cat", estimatedMinutes: 90, displayOrder: 6 },
      QUICK_CARE,
      ...SPA_SERVICES,
    ],
    addons: CORE_ADDONS,
  },

  // ── ③ 精細 ────────────────────────────────────────────────────────────
  // 7 bands, doodles split out from plain poodles (very different coat work),
  // over-cap surcharge armed, and 上門 enabled on the main services.
  {
    key: "detailed",
    emoji: "💎",
    name: "精細",
    nameEn: "Detailed",
    subtitle: "高階／連鎖",
    subtitleEn: "Premium / multi-branch",
    tagline: "7 級體型 · 7 組特殊品種 · 上門服務 · 超重加價",
    taglineEn: "7 size tiers · 7 breed groups · home visits · over-cap surcharge",
    desc: "最仔細嘅定價。7 級體型分得好密，貴婦、doodle、雙層毛、長毛貓等 7 組各有獨立價，主要服務開埋上門，超過 30kg 每 5kg 自動加 $100。啱高階或多分店。",
    descEn: "The most granular pricing. Seven tightly-spaced size tiers, separate rates for poodles, doodles, double coats and long-haired cats, home visits on the main services, and an automatic $100 per 5kg surcharge above 30kg.",
    tiers: [
      tier("t-xxs", "迷你", "Toy", 0.1, 3),
      tier("t-xs", "細小", "Mini", 3.1, 6),
      tier("t-s", "小型", "Small", 6.1, 9),
      tier("t-m", "中型", "Medium", 9.1, 13),
      tier("t-l", "大型", "Large", 13.1, 20),
      tier("t-xl", "超大", "X-Large", 20.1, 30),
      tier("t-xxl", "巨型", "Giant", 30.1, 999),
    ],
    coatSplit: true,
    hugeExtra: { baseW: 30, perStep: 5, stepPrice: 100 },
    breeds: [
      breed("sb-poodle", "貴婦系", "Poodle family", ["poodle", "poochon", "shihpoo"],
        ["貴婦犬", "比熊貴婦", "西施貴婦"]),
      breed("sb-doodle", "Doodle 混種", "Doodle crosses", ["goldendoodle", "labradoodle", "cockapoo", "maltipoo", "yorkipoo"],
        ["金毛貴婦", "拉布拉多貴婦", "可卡布", "馬爾貴婦", "約瑟貴婦"]),
      breed("sb-double", "雙層厚毛", "Double-coated", ["pomeranian", "samoyed", "husky", "golden", "shiba"],
        ["松鼠犬", "薩摩耶", "哈士奇", "金毛尋回", "柴犬"]),
      breed("sb-curly", "捲毛梗犬", "Curly terriers", ["bichon", "schnauzer", "westie"],
        ["比熊犬", "雪納瑞", "西高地白爹利"]),
      breed("sb-longdog", "長毛小型犬", "Long-coat toys", ["shih_tzu", "maltese", "yorkshire", "pekingese"],
        ["西施犬", "魔天使", "約瑟爹利", "北京犬"]),
      breed("sb-mainecoon", "緬因貓", "Maine Coon", ["maine_coon", "norwegian"],
        ["緬因貓", "挪威森林貓"]),
      breed("sb-longcat", "長毛貓", "Long-haired cats", ["persian", "ragdoll", "chinchilla", "himalayan", "domestic_long"],
        ["波斯貓", "布偶貓", "金吉拉", "喜馬拉雅貓", "家貓 (長毛)"]),
    ],
    services: [
      { name: "沖涼全套", nameEn: "Full bath", petType: "dog", estimatedMinutes: 60, displayOrder: 1, mobileAvailable: true },
      { name: "沖涼剪毛 - 留頭尾", nameEn: "Bath & haircut — head/tail kept", petType: "dog", estimatedMinutes: 120, displayOrder: 2, mobileAvailable: true },
      { name: "沖涼剪毛 - 半手剪", nameEn: "Bath & haircut — part scissor", petType: "dog", estimatedMinutes: 120, displayOrder: 3, mobileAvailable: true },
      { name: "沖涼剪毛 - 全手剪", nameEn: "Bath & haircut — full scissor", petType: "dog", estimatedMinutes: 150, displayOrder: 4, mobileAvailable: true },
      { name: "剃毛 / 短剪", nameEn: "Shave / short clip", petType: "dog", estimatedMinutes: 90, displayOrder: 5 },
      { name: "貓沖涼全套", nameEn: "Cat full bath", petType: "cat", estimatedMinutes: 60, displayOrder: 6 },
      { name: "貓沖涼剷毛 - 留頭尾手仔", nameEn: "Cat bath & shave — head/tail/paws kept", petType: "cat", estimatedMinutes: 90, displayOrder: 7 },
      { name: "貓獅子剪", nameEn: "Cat lion cut", petType: "cat", estimatedMinutes: 90, displayOrder: 8 },
      QUICK_CARE,
      ...SPA_SERVICES,
    ],
    addons: [
      ...CORE_ADDONS,
      { name: "深層護理", nameEn: "Deep conditioning", price: 150, estimatedMinutes: 20 },
      { name: "染色 / 造型", nameEn: "Colour / styling", price: 200, estimatedMinutes: 30 },
    ],
  },

  // ── ④ 貓專門 ──────────────────────────────────────────────────────────
  {
    key: "cat",
    emoji: "🐱",
    name: "貓專門",
    nameEn: "Cat only",
    subtitle: "只做貓",
    subtitleEn: "Cats only",
    tagline: "3 級體型 · 短/長毛分開 · 3 組貓品種",
    taglineEn: "3 size tiers · short/long coat · 3 cat breed groups",
    desc: "淨係做貓嘅店。3 級體重，短毛同長毛分開（貓嘅毛長差異比狗更影響工時），緬因、波斯系同無毛貓各有獨立價。",
    descEn: "For a cat-only shop. Three weight bands with separate short- and long-coat prices (coat length drives the work even more for cats than dogs), plus rates for Maine Coons, the Persian family and hairless breeds.",
    tiers: [
      tier("t-s", "細貓", "Small cat", 0.1, 4),
      tier("t-m", "中貓", "Medium cat", 4.1, 7),
      tier("t-l", "大貓", "Large cat", 7.1, 999),
    ],
    coatSplit: true,
    hugeExtra: { baseW: 8, perStep: 2, stepPrice: 0 },
    breeds: [
      breed("sb-mainecoon", "緬因貓", "Maine Coon", ["maine_coon", "norwegian", "savannah"],
        ["緬因貓", "挪威森林貓", "薩凡納貓"]),
      breed("sb-persian", "波斯系", "Persian family", ["persian", "himalayan", "chinchilla", "exotic_short"],
        ["波斯貓", "喜馬拉雅貓", "金吉拉", "異國短毛貓"]),
      breed("sb-hairless", "無毛 / 捲毛", "Hairless / rex", ["sphinx", "devon_rex"],
        ["斯芬克斯貓", "德文捲毛貓"]),
    ],
    services: [
      { name: "貓沖涼全套", nameEn: "Cat full bath", petType: "cat", estimatedMinutes: 60, displayOrder: 1 },
      { name: "貓沖涼剷毛 - 留頭尾手仔", nameEn: "Cat bath & shave — head/tail/paws kept", petType: "cat", estimatedMinutes: 90, displayOrder: 2 },
      { name: "貓獅子剪", nameEn: "Cat lion cut", petType: "cat", estimatedMinutes: 90, displayOrder: 3 },
      { ...QUICK_CARE, petType: "cat" },
    ],
    addons: [
      { name: "洗耳", nameEn: "Ear cleaning", price: 50, estimatedMinutes: 10 },
      { name: "剪甲", nameEn: "Nail trim", price: 50, estimatedMinutes: 10 },
      { name: "剃腳底", nameEn: "Paw-pad shave", price: 60, estimatedMinutes: 10 },
      { name: "解結", nameEn: "De-matting", price: 100, estimatedMinutes: 10 },
    ],
  },

  // ── ⑤ 白紙 ────────────────────────────────────────────────────────────
  // Still ships the 5 default tiers — an empty tier list would leave the
  // matrix editor with nothing to render.
  {
    key: "blank",
    emoji: "📄",
    name: "白紙",
    nameEn: "Blank",
    subtitle: "自己由零開始",
    subtitleEn: "Start from scratch",
    tagline: "5 級預設體型 · 1 個示範服務",
    taglineEn: "5 default tiers · 1 sample service",
    desc: "唔用模板。只保留 5 級預設體型同一個示範服務，其餘全部自己加。",
    descEn: "No template. Keeps the five default size tiers and one sample service; you add everything else yourself.",
    tiers: [
      tier("t-s", "小型", "Small", 0.1, 5),
      tier("t-m", "中型", "Medium", 5.1, 10),
      tier("t-l", "大型", "Large", 10.1, 15),
      tier("t-xl", "超大", "X-Large", 15.1, 20),
      tier("t-xxl", "巨型", "Giant", 20.1, 999),
    ],
    coatSplit: false,
    hugeExtra: { baseW: 25, perStep: 5, stepPrice: 0 },
    breeds: [],
    services: [
      { name: "沖涼全套", nameEn: "Full bath", petType: "dog", estimatedMinutes: 60, displayOrder: 1 },
    ],
    addons: [],
  },
];

// Localised card copy for the picker.
export const templateLabels = (t) => ({
  name: pickName(t.name, t.nameEn),
  subtitle: pickName(t.subtitle, t.subtitleEn),
  tagline: pickName(t.tagline, t.taglineEn),
  desc: pickName(t.desc, t.descEn),
});

export const getTemplate = (key) =>
  SETTING_TEMPLATES.find(t => t.key === key) || SETTING_TEMPLATES.find(t => t.recommended);

// ── price carry-over ────────────────────────────────────────────────────
// Changing the tier list changes the byTier KEYS, so a naive keep-prices
// would strand every number the owner typed. Instead each NEW tier inherits
// from whichever OLD tier covered its midpoint — a 5→7 split lands sensible
// starting prices in all 7 bands, and a 7→5 merge keeps the price of the band
// the merged range sits in.
const midOf = (t) => {
  const lo = Number(t.minW) || 0;
  const hi = Math.min(Number(t.maxW) || 999, 200); // clamp the open-ended top band
  return (lo + hi) / 2;
};

function oldEntriesByTier(mp) {
  // Accepts either the current byTier map or the legacy `sizes` array, and
  // returns [{minW,maxW,entry}] so the remap works from either format.
  if (!mp) return [];
  if (mp.byTier && typeof mp.byTier === "object" && Object.keys(mp.byTier).length) {
    return null; // caller supplies the old tier list for these
  }
  if (Array.isArray(mp.sizes) && mp.sizes.length) {
    return mp.sizes.map(s => ({
      minW: Number(s.minW) || 0,
      maxW: Number(s.maxW) || 999,
      entry: { short: Number(s.shortHair) || 0, long: Number(s.longHair) || Number(s.shortHair) || 0 },
    }));
  }
  return [];
}

// Build [{minW,maxW,entry}] ranges for an existing service, from whichever
// storage format it uses.
function rangesFor(mp, oldTiers) {
  const legacy = oldEntriesByTier(mp);
  if (legacy) return legacy;
  return (oldTiers || []).map(t => ({
    minW: Number(t.minW) || 0,
    maxW: Number(t.maxW) || 999,
    entry: (mp.byTier || {})[t.id] || null,
  })).filter(r => r.entry);
}

const priceOf = (e) => Math.max(Number(e?.short) || 0, Number(e?.long) || 0);

function remapRanges(ranges, newTiers) {
  const out = {};
  if (!ranges.length) return out;
  for (const t of newTiers) {
    const lo = Number(t.minW) || 0;
    const hi = Math.min(Number(t.maxW) || 999, 200);
    // Pick the old band that overlaps this new band the most. On a SPLIT the
    // new band sits wholly inside one old band, so this is just "the band it
    // came from". On a MERGE it is the band that covers most of the new
    // range — which midpoint-matching gets wrong when the midpoint happens to
    // land in the 0.1kg seam between two old bands.
    let best = null, bestOverlap = -1;
    for (const r of ranges) {
      const ov = Math.min(hi, Math.min(r.maxW, 200)) - Math.max(lo, r.minW);
      if (ov > bestOverlap || (ov === bestOverlap && best && priceOf(r.entry) > priceOf(best.entry))) {
        // Ties go to the dearer band: a merged band now also covers the
        // bigger pets, and quietly under-pricing them is the worse default.
        best = r; bestOverlap = ov;
      }
    }
    if (bestOverlap < 0 || !best) {
      // No overlap at all (the new tiers reach past where the old ones
      // stopped) — fall back to the nearest band by midpoint.
      const mid = midOf(t);
      best = ranges.slice().sort((a, b) =>
        Math.abs(mid - (a.minW + Math.min(a.maxW, 200)) / 2) -
        Math.abs(mid - (b.minW + Math.min(b.maxW, 200)) / 2))[0];
    }
    if (best && best.entry) out[t.id] = { ...best.entry };
  }
  return out;
}

const catalogIdsOf = (b) => (Array.isArray(b?.catalogBreedIds) ? b.catalogBreedIds
  : (b?.catalogBreedId ? [b.catalogBreedId] : []));

// Which OLD breed row a new template breed row inherits its prices from.
// Matching on row id alone is not enough: a shop's own rows carry generated
// ids (sb-b25d849d3e), so "貴婦犬 / 可卡布" would never match the template's
// sb-poodle and the owner would silently lose every special-breed price they
// had typed. Match on the catalog breeds the rows have in common instead,
// and take the row that shares the most.
function oldBreedRowFor(newBreed, oldBreeds, oldByBreed) {
  if (oldByBreed[newBreed.id]) return oldByBreed[newBreed.id];   // same row id
  const want = new Set(catalogIdsOf(newBreed));
  if (!want.size) return null;
  let best = null, bestShared = 0;
  for (const ob of oldBreeds || []) {
    const entry = oldByBreed[ob.id];
    if (!entry) continue;
    const shared = catalogIdsOf(ob).filter(x => want.has(x)).length;
    if (shared > bestShared) { best = entry; bestShared = shared; }
  }
  return bestShared > 0 ? best : null;
}

// Carry a service's whole matrix (tier prices + per-breed prices) onto the
// template's new tier list.
function carryMatrix(oldMp, oldTiers, newTiers, newBreeds, oldBreeds) {
  const byTier = remapRanges(rangesFor(oldMp, oldTiers), newTiers);
  const byBreed = {};
  const oldByBreed = (oldMp && oldMp.byBreed) || {};
  for (const b of newBreeds) {
    const cur = oldBreedRowFor(b, oldBreeds, oldByBreed);
    if (!cur) continue;
    const ranges = (oldTiers || []).map(t => ({
      minW: Number(t.minW) || 0,
      maxW: Number(t.maxW) || 999,
      entry: cur[t.id] || null,
    })).filter(r => r.entry);
    const mapped = remapRanges(ranges, newTiers);
    if (Object.keys(mapped).length) byBreed[b.id] = mapped;
  }
  return { byTier, byBreed };
}

const normName = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, "");

// A template row matches an existing service/add-on under EITHER language.
// A shop that was seeded in Chinese and later re-applies a template in
// English (or the reverse) must land on the same rows, or every service
// would be duplicated and the ids that appointments point at abandoned.
const aliasesOf = (row) => [row.name, row.nameEn].filter(Boolean).map(normName);
const matchRow = (list, row) => {
  const want = aliasesOf(row);
  return list.find(e => want.includes(normName(e.name)));
};
// The name this row should be WRITTEN as, in the current language.
const nameFor = (row) => pickName(row.name, row.nameEn);

// ── what would change ───────────────────────────────────────────────────
// Drives the confirm dialog. Pure — touches nothing.
export function previewTemplate({ tenant, template, extraServices = "keep" }) {
  const t = typeof template === "string" ? getTemplate(template) : template;
  const oldTiers = (tenant?.pricingConfig?.tiers || []).length ? tenant.pricingConfig.tiers : [];
  const existing = (tenant?.services || []).filter(Boolean);
  const tplNames = new Set(t.services.flatMap(aliasesOf));
  const matched = existing.filter(s => tplNames.has(normName(s.name)));
  // A template turns every service it covers back ON. If the owner had
  // deliberately switched one off (they don't do cats, say), that is a
  // surprise worth naming in the confirm dialog rather than doing silently.
  const reEnabled = matched.filter(s => s.enabled === false);
  const extras = existing.filter(s => !tplNames.has(normName(s.name)) && s.enabled !== false);
  const added = t.services.filter(s => !matchRow(existing, s));
  return {
    template: t,
    tierFrom: oldTiers.length || 5,
    tierTo: t.tiers.length,
    tierChanged: oldTiers.length !== t.tiers.length,
    coatSplitFrom: tenant?.pricingConfig?.coatSplit === true,
    coatSplitTo: t.coatSplit === true,
    breedFrom: (tenant?.specialBreeds || []).length,
    breedTo: t.breeds.length,
    keptServices: matched.length,
    reEnabledServices: reEnabled.map(s => s.name),
    addedServices: added.map(nameFor),
    extraServices: extras.map(s => s.name),
    rebandedServices: extras.filter(s => s.pricingMode !== "static" && s.matrixPricing).map(s => s.name),
    willDisable: extraServices === "disable" ? extras.map(s => s.name) : [],
  };
}

// ── undo snapshot ───────────────────────────────────────────────────────
export function snapshotTemplateState(tenant) {
  return {
    at: Date.now(),
    pricingConfig: JSON.parse(JSON.stringify(tenant?.pricingConfig || {})),
    specialBreeds: JSON.parse(JSON.stringify(tenant?.specialBreeds || [])),
    services: (tenant?.services || []).map(s => ({
      id: s.id,
      matrixPricing: s.matrixPricing ? JSON.parse(JSON.stringify(s.matrixPricing)) : null,
      pricingMode: s.pricingMode,
      enabled: s.enabled,
      mobileAvailable: !!s.mobileAvailable,
      displayOrder: s.displayOrder,
    })),
    addons: (tenant?.addons || []).map(a => ({ id: a.id, price: a.price, enabled: a.enabled })),
  };
}

export async function restoreTemplateSnapshot({ tenantId, tenant, snapshot }) {
  await patchTenant(tenantId, {
    pricingConfig: snapshot.pricingConfig,
    specialBreeds: snapshot.specialBreeds,
  });
  const byId = new Map((tenant?.services || []).map(s => [s.id, s]));
  const out = [];
  for (const snap of snapshot.services) {
    const live = byId.get(snap.id);
    if (!live) continue;
    out.push(await upsertService({
      ...live,
      tenantId,
      matrixPricing: snap.matrixPricing,
      pricingMode: snap.pricingMode,
      enabled: snap.enabled,
      mobileAvailable: snap.mobileAvailable,
      displayOrder: snap.displayOrder,
    }));
  }
  // Services the template CREATED have no snapshot to restore, so without
  // this they survive the undo — enabled, customer-visible and priced at $0,
  // which puts them straight onto the public booking page. Switch them off
  // instead of deleting: by the time the owner undoes, one may already be on
  // a booking, and a disabled service still resolves for that appointment.
  const snapIds = new Set(snapshot.services.map(s => s.id));
  for (const live of (tenant?.services || [])) {
    if (snapIds.has(live.id) || live.enabled === false) continue;
    out.push(await upsertService({ ...live, tenantId, enabled: false }));
  }
  return {
    tenantPatch: { pricingConfig: snapshot.pricingConfig, specialBreeds: snapshot.specialBreeds },
    services: out,
  };
}

// ── apply ───────────────────────────────────────────────────────────────
// SAFETY: services are never deleted. `appointments.service_id`,
// `pets.special_prices`, `pets.blocked_service_ids`, `profiles.can_service_ids`
// and 套票 all point at service ids, and deleteService is a hard DELETE — so a
// re-apply matches by name to keep the existing id, and anything the template
// doesn't cover is either left alone (default) or disabled, never removed.
// Historical appointments snapshot service_name / base_price / addon_price, so
// changing tiers and prices can't rewrite the past.
export async function applyTemplate({
  tenantId,
  tenant,
  template,
  keepPrices = true,
  extraServices = "keep",
  onProgress,
}) {
  const t = typeof template === "string" ? getTemplate(template) : template;
  const oldTiers = (tenant?.pricingConfig?.tiers || []).length ? tenant.pricingConfig.tiers : [];
  const oldBreeds = Array.isArray(tenant?.specialBreeds) ? tenant.specialBreeds : [];
  const errors = [];
  const step = (msg) => { try { onProgress && onProgress(msg); } catch { /* ignore */ } };

  // 1. Shop-level: tiers, coat split, over-cap surcharge, breed rows.
  const tenantPatch = {
    pricingConfig: {
      ...(tenant?.pricingConfig || {}),
      tiers: t.tiers.map(x => ({ ...x })),
      coatSplit: !!t.coatSplit,
      hugeExtra: { ...t.hugeExtra },
      // Which template this shop last applied — drives the "目前：標準" badge
      // in the picker. Lives inside pricingConfig because patchTenant only
      // maps known tenant columns and pricing_config is a free-form jsonb.
      templateKey: t.key,
    },
    specialBreeds: t.breeds.map(b => JSON.parse(JSON.stringify(b))),
  };
  step("套用體型分級…");
  try {
    await patchTenant(tenantId, tenantPatch);
  } catch (e) {
    errors.push("tenant:" + (e?.message || e));
  }

  // 2. Services — match on name so ids (and everything pointing at them) hold.
  const existing = (tenant?.services || []).filter(Boolean);
  const savedServices = [];
  step("套用服務…");
  for (const svc of t.services) {
    const prev = matchRow(existing, svc);
    const isStatic = svc.pricingMode === "static";
    let matrixPricing = null;
    if (!isStatic) {
      matrixPricing = keepPrices && prev
        ? carryMatrix(prev.matrixPricing, oldTiers, t.tiers, t.breeds, oldBreeds)
        : { byTier: {}, byBreed: {} };
    }
    try {
      savedServices.push(await upsertService({
        id: prev?.id || uuid(),
        tenantId,
        // Keep whatever the row is already called — renaming a shop's
        // service out from under it just because the UI language changed
        // would be worse than a mixed-language list.
        name: prev?.name || nameFor(svc),
        kind: svc.kind || "service",
        description: prev?.description || pickName(svc.description, svc.descriptionEn) || "",
        basePrice: isStatic ? 0 : (keepPrices ? (prev?.basePrice ?? 0) : 0),
        estimatedMinutes: svc.estimatedMinutes ?? prev?.estimatedMinutes ?? 60,
        bufferMinutes: prev?.bufferMinutes ?? 0,
        mobileAvailable: svc.mobileAvailable === undefined ? (prev?.mobileAvailable ?? false) : !!svc.mobileAvailable,
        pricingMode: isStatic ? "static" : "matrix",
        matrixPricing,
        smartPricing: prev?.smartPricing || { enabled: false, rules: [] },
        enabled: true,
        displayOrder: svc.displayOrder,
        petType: svc.petType || "dog",
        petTypes: svc.petTypes,
        customerVisible: svc.customerVisible !== false,
      }));
    } catch (e) {
      errors.push("svc:" + svc.name + ":" + (e?.message || e));
    }
  }

  // 3. Services the template doesn't cover — the shop's own creations.
  //
  // These still have to be REMAPPED even though the template does not own
  // them, because tiers are shop-level. Leaving a service on the old tier
  // keys does not leave its prices alone: `t-s` used to mean 0.1–5kg and now
  // means 6.1–9kg, so an untouched service would quietly charge the old
  // small-dog price for a 7kg dog and have no price at all for a 3kg one.
  // Their prices are carried, never wiped — keepPrices governs the
  // template's own services, not the owner's.
  //
  // Disabling is separate, opt-in, and still reversible from 服務 (the row
  // and its id survive; nothing is ever deleted).
  const tplNames = new Set(t.services.flatMap(aliasesOf));
  const disabled = [];
  const extras = existing.filter(e => !tplNames.has(normName(e.name)));
  if (extras.length) step("更新其他服務嘅體型分級…");
  for (const e of extras) {
    const turnOff = extraServices === "disable" && e.enabled !== false;
    const hasMatrix = e.pricingMode !== "static" && e.matrixPricing;
    if (!turnOff && !hasMatrix) continue;   // nothing to do for this one
    try {
      savedServices.push(await upsertService({
        ...e,
        tenantId,
        enabled: turnOff ? false : e.enabled,
        matrixPricing: hasMatrix
          ? carryMatrix(e.matrixPricing, oldTiers, t.tiers, t.breeds, oldBreeds)
          : e.matrixPricing,
      }));
      if (turnOff) disabled.push(e.name);
    } catch (err) {
      errors.push("extra:" + e.name + ":" + (err?.message || err));
    }
  }

  // 4. Add-ons — matched by name too. Add-on prices are real defaults (not 0),
  //    so keepPrices protects an owner's edited price from being reset.
  const existingAddons = (tenant?.addons || []).filter(Boolean);
  const savedAddons = [];
  step("套用加購…");
  for (const ad of t.addons) {
    const prev = matchRow(existingAddons, ad);
    try {
      savedAddons.push(await upsertAddon({
        id: prev?.id || uuid(),
        tenantId,
        name: prev?.name || nameFor(ad),
        price: keepPrices && prev ? (prev.price ?? ad.price) : ad.price,
        pricingMode: prev?.pricingMode || "flat",
        variants: prev?.variants || [],
        enabled: true,
        estimatedMinutes: ad.estimatedMinutes ?? prev?.estimatedMinutes ?? 10,
      }));
    } catch (e) {
      errors.push("addon:" + ad.name + ":" + (e?.message || e));
    }
  }

  if (errors.length) {
    console.warn("[PettoBook template] partial errors:", errors);
  }
  return { tenantPatch, services: savedServices, addons: savedAddons, disabled, errors };
}
